// @spec AST-STR-006
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { ImportHistoryTable } from '@/features/assets/components/ImportHistoryTable';
import { ImportWizardDialog } from '@/features/assets/components/ImportWizardDialog';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/assets/import')({
  validateSearch: (search: Record<string, unknown>) => ({
    uploadId: typeof search.uploadId === 'string' ? search.uploadId : undefined,
  }),
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.importAssets'),
  }),
  component: AssetImportPage,
});

/**
 * Asset import page - bulk import wizard and history.
 */
function AssetImportPage() {
  const { uploadId } = Route.useSearch();
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const [isWizardOpen, setIsWizardOpen] = React.useState(!!uploadId);

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Handle back navigation
  const handleBack = React.useCallback(() => {
    void navigate({ to: '/assets' });
  }, [navigate]);

  // Handle successful import
  const handleImportSuccess = React.useCallback(() => {
    // Could navigate to assets list filtered to draft status
    void navigate({ to: '/assets', search: { status: ['draft'] } });
  }, [navigate]);

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">No Organization Selected</h2>
            <p className="mt-2 text-muted-foreground">
              Please select an organization to import assets.
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              Go to Organizations
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <header className="space-y-4">
          <Button variant="ghost" size="sm" onClick={handleBack} className="pl-0">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Assets
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Import Assets</h1>
              <p className="mt-1 text-muted-foreground">
                Bulk import assets from CSV or Excel files
              </p>
            </div>
            <Button
              onClick={() => {
                setIsWizardOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Import
            </Button>
          </div>
        </header>

        {/* Import Wizard Dialog */}
        <ImportWizardDialog
          isOpen={isWizardOpen}
          onClose={() => {
            setIsWizardOpen(false);
          }}
          organizationId={activeOrg.id}
          onSuccess={handleImportSuccess}
          uploadId={uploadId}
        />

        {/* Import History */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Import History</h2>
          <ImportHistoryTable />
        </section>
      </div>
    </div>
  );
}
